import {
  LinkModeChartWidgetHeaderBarButtonsProps,
  EditModeChartWidgetHeaderBarProps,
  AlertMessageProps,
} from './EditModeChartWidgetHeaderBar';
import { ArdoqId, WidgetTypes } from '@ardoq/api-types';
import { TrackEvent } from '../../types';
import { DashboardFilterParameter } from '../../types';
import { WidgetLinkedDashboardFilter } from '../types';
import { isActiveLinkedDashboardFilter } from '../utils';
import { IconName } from '@ardoq/icons';
import { formatListAsEnglishProse, isTruthy } from '@ardoq/common-helpers';
import { DashboardTrackingEventsNames } from '../../tracking';
import { type I18nLocale, t } from '@ardoq/i18n';

type LinkModeProps = {
  linkModeDashboardFilterParameter: DashboardFilterParameter | undefined;
  isInLinkDashboardFiltersMode?: boolean;
  isLinkedToLinkModeDashboardFilter: boolean;
  isLinkToLinkModeDashboardFilterSupported: boolean;
  isLinkModeDashboardFilterFieldUsedInWidgetFilter: boolean;
  isLinkModeDashboardFilterFieldUsedInWidgetCondition: boolean;
};

type GetEditModeChartHeaderBarPropsParams = {
  name?: string;
  widgetId: ArdoqId;
  widgetType: Exclude<WidgetTypes, WidgetTypes.HEADER | WidgetTypes.NOTE>;
  canEditWidget: boolean;
  cannotEditMessage?: string;
  linkedDashboardFilters: WidgetLinkedDashboardFilter[];
  linkModeProps: LinkModeProps;
  isEditDisabled: boolean;
  commands: {
    selectWidget: (id: ArdoqId) => void;
    editWidgetLinkModeDashboardFilterBinding: (widgetId: ArdoqId) => void;
    unlinkWidgetWithDashboardFilter: (
      widgetId: ArdoqId,
      dashboardFilterParameterId: ArdoqId
    ) => void;
    linkWidgetWithDashboardFilter: (
      widgetId: ArdoqId,
      dashboardFilterParameterId: ArdoqId
    ) => void;
    copyWidget: (id: ArdoqId) => void;
    deleteWidget: (id: ArdoqId, widgetType?: WidgetTypes) => void;
  };
  isSaving: boolean;
  trackEvent: TrackEvent;
  confidentialFieldLabels: string[];
  i18nLocale: I18nLocale;
};

type EditModeChartHeaderBarCommands = {
  selectWidget: () => void;
  editWidgetLinkModeDashboardFilterBinding: () => void;
  unlinkWidgetWithDashboardFilter: (() => void) | undefined;
  linkWidgetWithDashboardFilter: (() => void) | undefined;
};

const getLinkModeChartWidgetHeaderButtonsProps = ({
  commands,
  linkModeProps: {
    isLinkedToLinkModeDashboardFilter,
    isLinkToLinkModeDashboardFilterSupported,
    isLinkModeDashboardFilterFieldUsedInWidgetFilter,
    isLinkModeDashboardFilterFieldUsedInWidgetCondition,
    linkModeDashboardFilterParameter,
  },
  i18nLocale,
}: {
  linkModeProps: LinkModeProps;
  commands: EditModeChartHeaderBarCommands;
  i18nLocale: I18nLocale;
}): LinkModeChartWidgetHeaderBarButtonsProps => {
  const isDashboardFilterAlreadyAssociatedWithField = Boolean(
    linkModeDashboardFilterParameter?.fieldApiName
  );

  const showToggle =
    (isLinkToLinkModeDashboardFilterSupported &&
      isDashboardFilterAlreadyAssociatedWithField) ||
    isLinkedToLinkModeDashboardFilter;

  const linkButtonStatus = {
    isSetupButton:
      isLinkToLinkModeDashboardFilterSupported &&
      !isDashboardFilterAlreadyAssociatedWithField,
    isReviewButton:
      isLinkToLinkModeDashboardFilterSupported &&
      isLinkModeDashboardFilterFieldUsedInWidgetFilter,
    isDetailsButton:
      isLinkedToLinkModeDashboardFilter ||
      (isLinkToLinkModeDashboardFilterSupported &&
        isDashboardFilterAlreadyAssociatedWithField &&
        !isLinkModeDashboardFilterFieldUsedInWidgetFilter),
  };

  const showLinkButton =
    linkButtonStatus.isSetupButton ||
    linkButtonStatus.isReviewButton ||
    linkButtonStatus.isDetailsButton;

  const linkButtonText = linkButtonStatus.isSetupButton
    ? t(i18nLocale, 'dashboard:linkMode.setUpLink', 'Set up link')
    : linkButtonStatus.isReviewButton
      ? t(i18nLocale, 'dashboard:linkMode.reviewLink', 'Review link')
      : t(i18nLocale, 'dashboard:linkMode.linkDetails', 'Link details');

  const linkToggleTooltip = isLinkModeDashboardFilterFieldUsedInWidgetCondition
    ? t(
        i18nLocale,
        'dashboard:linkMode.tooltipPercentageCondition',
        'This column is used by the percentage condition.'
      )
    : isLinkModeDashboardFilterFieldUsedInWidgetFilter
      ? t(
          i18nLocale,
          'dashboard:linkMode.tooltipReplaceWidgetFilter',
          'To link this chart, replace its chart filter by clicking "Review link"'
        )
      : '';

  return {
    linkToggle: showToggle
      ? {
          isDisabled:
            isLinkModeDashboardFilterFieldUsedInWidgetFilter ||
            isLinkModeDashboardFilterFieldUsedInWidgetCondition,
          tooltip: linkToggleTooltip,
          isChecked: isLinkedToLinkModeDashboardFilter,
          label: isLinkedToLinkModeDashboardFilter
            ? t(i18nLocale, 'dashboard:linkMode.linked', 'Linked')
            : t(i18nLocale, 'dashboard:linkMode.notLinked', 'Not linked'),
          name: isLinkedToLinkModeDashboardFilter ? 'linked' : 'not linked',
          onChange: () =>
            isLinkedToLinkModeDashboardFilter
              ? commands.unlinkWidgetWithDashboardFilter?.()
              : commands.linkWidgetWithDashboardFilter?.(),
        }
      : null,
    linkButton: showLinkButton
      ? {
          text: linkButtonText,
          icon: !linkButtonStatus.isDetailsButton
            ? IconName.ADD_LINK
            : undefined,
          onClick: commands.editWidgetLinkModeDashboardFilterBinding,
        }
      : null,
  };
};

const confidentialFieldsToAlertMessage = (
  confidentialFieldLabels: string[],
  i18nLocale: I18nLocale
): AlertMessageProps | undefined => {
  const count = confidentialFieldLabels.length;
  if (count === 0) return;

  const title =
    count === 1
      ? t(
          i18nLocale,
          'dashboard:widgetEditor.confidentialFieldVisible',
          'Confidential field visible'
        )
      : t(
          i18nLocale,
          'dashboard:widgetEditor.confidentialFieldsVisible',
          'Confidential fields visible'
        );

  const formattedFieldNames = formatListAsEnglishProse(confidentialFieldLabels);
  const warningMessage =
    count === 1
      ? t(
          i18nLocale,
          'dashboard:widgetEditor.confidentialFieldVisibleSingleMessage',
          'This field is visible to everyone who can access this dashboard.'
        )
      : t(
          i18nLocale,
          'dashboard:widgetEditor.confidentialFieldsVisiblePluralMessage',
          { fieldNames: formattedFieldNames },
          '{{fieldNames}} are visible to everyone who can access this dashboard.'
        );

  return {
    iconName: IconName.CONTROL,
    messages: [
      {
        text: title,
        highlight: title,
      },
      {
        text: warningMessage,
        highlight: count === 1 ? undefined : formattedFieldNames,
      },
    ],
  };
};

const toAlertMessages = (
  confidentialFieldLabels: string[],
  i18nLocale: I18nLocale
): AlertMessageProps[] => {
  return [
    confidentialFieldsToAlertMessage(confidentialFieldLabels, i18nLocale),
  ].filter(isTruthy);
};

export const getEditModeChartWidgetHeaderBarProps = ({
  linkModeProps,
  widgetId,
  commands,
  linkedDashboardFilters,
  trackEvent,
  i18nLocale,
  ...params
}: GetEditModeChartHeaderBarPropsParams): EditModeChartWidgetHeaderBarProps => {
  const linkModeDashboardFilterParameterId =
    linkModeProps.linkModeDashboardFilterParameter?.id;
  const chartWidgetCommands = {
    selectWidget: () => commands.selectWidget(widgetId),
    copyWidget: () => {
      trackEvent(
        DashboardTrackingEventsNames.DUPLICATED_CHART_IN_DASHBOARD_BUILDER
      );
      commands.copyWidget(widgetId);
    },
    deleteWidget: () => {
      trackEvent(
        DashboardTrackingEventsNames.DELETED_CHART_IN_DASHBOARD_BUILDER
      );
      commands.deleteWidget(widgetId, params.widgetType);
    },
    editWidgetLinkModeDashboardFilterBinding: () =>
      commands.editWidgetLinkModeDashboardFilterBinding(widgetId),
    unlinkWidgetWithDashboardFilter: linkModeDashboardFilterParameterId
      ? () =>
          commands.unlinkWidgetWithDashboardFilter(
            widgetId,
            linkModeDashboardFilterParameterId
          )
      : undefined,
    linkWidgetWithDashboardFilter: linkModeDashboardFilterParameterId
      ? () =>
          commands.linkWidgetWithDashboardFilter(
            widgetId,
            linkModeDashboardFilterParameterId
          )
      : undefined,
  };

  const linkModeButtons = getLinkModeChartWidgetHeaderButtonsProps({
    linkModeProps,
    commands: chartWidgetCommands,
    i18nLocale,
  });

  return {
    name: params.name,
    i18nLocale,
    isEditDisabled: params.isEditDisabled,
    canEditWidget: params.canEditWidget,
    cannotEditMessage: params.cannotEditMessage,
    activeLinkedDashboardFilters:
      linkedDashboardFilters?.filter(isActiveLinkedDashboardFilter) ?? [],
    linkModeProps: {
      isInLinkDashboardFiltersMode:
        linkModeProps.isInLinkDashboardFiltersMode ?? false,
      linkModeButtons,
    },
    editButtonTooltipText:
      params.canEditWidget && !params.isEditDisabled
        ? t(i18nLocale, 'dashboard:widgetEditor.editChart', 'Edit chart')
        : undefined,
    isSaving: params.isSaving,
    commands: chartWidgetCommands,
    alertMessages: toAlertMessages(params.confidentialFieldLabels, i18nLocale),
  };
};
